import { useEffect } from "react";
import { useApi } from "../api/useApi";
import { CaseExpense, useInitialValues, UserAccount } from "../types";
import { useGuid } from "../../utilities/useGuid";
import { useToasts } from "../../components/toasts/useToasts";
import { useUtilities } from "../../utilities/useUtilities";
import { useAccess } from "../../auth/useAccess";
import { FileUploadManager } from "./FileUploadManager";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { useCaseExpenseState } from "../../redux/reducers/caseExpenseReducer";
import { useCaseExpenseActions } from "../../redux/actions/caseExpenseActions";

export const CaseExpenseApiRoutes = {
    getCaseExpense: (accountId: string, caseId: string, expenseId: string) => `account/${accountId}/case/${caseId}/expense/${expenseId}`,
    getCaseExpenses: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}`,
    upsertCaseExpense: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/expense`,
    deleteCaseExpense: (accountId: string, caseId: string, expenseId: string) => `account/${accountId}/case/${caseId}/expense/${expenseId}`,
    deleteCaseExpenses: (accountId: string, caseId: string) => `account/${accountId}/case/${caseId}/expenses`,
}

export type CaseExpenseService = {
    expenses: CaseExpense[];
    getCaseExpenseAsync: (expenseId: string) => Promise<CaseExpense | undefined>;
    loadCaseExpensesAsync: (caseId?: string) => Promise<void>;
    loadExpensesForCasesAsync: (caseIds?: string[]) => Promise<void>;
    upsertCaseExpenseAsync: (caseExpense: CaseExpense, attachment?: File) => Promise<void>;
    deleteCaseExpenseAsync: (expenseId: string) => Promise<void>;
    deleteCaseExpensesAsync: (expenseIds: string[]) => Promise<void>;
    markExpenseRecoverableAsync: (expenseId: string, recoverable: boolean) => Promise<void>;
}
/**
 * This service will load expenses for a specific case and provides methods to interact with them
 * @param caseId 
 * @param autoload
 * @returns
 */
export const useCaseExpenseService = (caseId?: string): CaseExpenseService => {

    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance();

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const { account } = primaryAccount as UserAccount ?? {};
    const expenseState = useCaseExpenseState();
    const expenseActions = useCaseExpenseActions();
    const guid = useGuid();
    const toasts = useToasts();
    const utils = useUtilities();
    const initValues = useInitialValues();

    //Api
    const api = useApi<CaseExpense>('api/CaseExpense');

    //Data Calls
    const loadCaseExpensesAsync = async (myCaseId?: string) => {
        const expenses = await api.getAsync(CaseExpenseApiRoutes.getCaseExpenses(account?.accountId as string, myCaseId || caseId as string));
        expenseActions.setCaseExpenses(expenses);
    }
    const loadExpensesForCasesAsync = async (caseIds?: string[]) => {
        const uniqueCaseIds = [...new Set((caseIds || []).filter(Boolean))];

        if (!uniqueCaseIds.length) {
            expenseActions.setCaseExpenses([]);
            return;
        }

        const expenseGroups = await Promise.all(
            uniqueCaseIds.map((id) =>
                api.getAsync(CaseExpenseApiRoutes.getCaseExpenses(account?.accountId as string, id)),
            ),
        );

        const allExpenses = expenseGroups.flat();
        const dedupedExpenses = allExpenses.reduce((acc: CaseExpense[], expense: CaseExpense) => {
            if (!expense?.expenseId) return acc;

            const existingIndex = acc.findIndex((x: CaseExpense) => x.expenseId === expense.expenseId);
            if (existingIndex >= 0) {
                acc[existingIndex] = expense;
            } else {
                acc.push(expense);
            }

            return acc;
        }, [] as CaseExpense[]);

        expenseActions.setCaseExpenses(dedupedExpenses);
    }
    const getCaseExpenseAsync = async (expenseId: string) => {
        let expense = (expenseState.expenses || []).find(e => e.expenseId === expenseId);
        if (!expense) {
            expense = await api.getAsync(CaseExpenseApiRoutes.getCaseExpense(account?.accountId as string, caseId as string, expenseId));
            if(expense) {
                expenseActions.addOrReplaceCaseExpense(expense);
            }
        }
        return expense;
    }
    const upsertCaseExpenseAsync = async (caseExpense: CaseExpense, attachment?: File) => {
        if (!!attachment) {
            const fileId = guid.newGuid();
            const fileUploadInfo = initValues.fileUploadInfo(caseExpense.caseId as string, fileId, attachment, true);
            await fileUploadManager.uploadFileAsync(fileUploadInfo);

            caseExpense.attachmentId = fileId;
        }

        expenseActions.addOrReplaceCaseExpense(caseExpense);
        const updatedExp = await api.postAsync(caseExpense, CaseExpenseApiRoutes.upsertCaseExpense(account?.accountId as string, caseExpense.caseId as string));
        expenseActions.addOrReplaceCaseExpense(updatedExp);

        toasts.notifySuccess("Expense Updated", "Case Expense was updated.", `${caseExpense.type} - ${utils.formatCurrency(caseExpense.amount)}`);
    }
    const markExpenseRecoverableAsync = async (expenseId: string, recoverable: boolean) => {
        const expense = await getCaseExpenseAsync(expenseId);
        if (expense) {
            const updateExp = { ...expense, recoverable };
            await upsertCaseExpenseAsync(updateExp);
        }
    }
    const deleteCaseExpenseAsync = async (expenseId: string) => {
        expenseActions.removeCaseExpense(expenseId);
        await api.deleteAsync([expenseId], CaseExpenseApiRoutes.deleteCaseExpense(account?.accountId as string, caseId as string, expenseId));
    }
    const deleteCaseExpensesAsync = async (expenseIds: string[]) => {
        if (expenseIds.length) {
            expenseActions.removeCaseExpenses(expenseIds);
            await api.deleteAsync(expenseIds, CaseExpenseApiRoutes.deleteCaseExpenses(account?.accountId as string, caseId as string));
        }
    }
        
    //Logic
    const filterExpenses = (exp: CaseExpense) => {
        if (access.account.canViewAllCases()) return true;

        return exp?.username == primaryAccount?.user.username || access.cases.canEditCaseAsAdmin(caseId || exp?.caseId);
    }
     
    //Effects
    useEffect(() => {
        fileUploadManager.setAccount(primaryAccount?.account.accountId || '');
    }, [primaryAccount?.account.accountId]);

    return {
        expenses: (expenseState.expenses || []).filter(filterExpenses),
        loadCaseExpensesAsync,
        loadExpensesForCasesAsync,
        getCaseExpenseAsync,
        upsertCaseExpenseAsync,
        markExpenseRecoverableAsync,
        deleteCaseExpenseAsync,
        deleteCaseExpensesAsync
    }
}
